import { Dropdown, Flex, Thumbnail } from '@bynder/design-system';
import type { Editor } from '@tiptap/react';
import {
  type ColorOption,
  DEFAULT_COLOR_OPTIONS,
} from '../../data/configs/colors';
import { getValueOfCssVariable } from '../../helpers/dom';
import { isTableCellBgActive } from '../../helpers/tiptap';

type ColorPickerDropdownProps = {
  onChange: (color: ColorOption) => void;
  editor: Editor;
  colorOptions?: ColorOption[];
};

export const CellColorPickerDropdown = ({
  editor,
  onChange,
  colorOptions,
}: ColorPickerDropdownProps) => {
  const colors =
    colorOptions && Array.isArray(colorOptions) && colorOptions.length !== 0
      ? colorOptions
      : DEFAULT_COLOR_OPTIONS;

  const defaultColorOption = { label: 'Default', value: '' };
  const combinedColors = [defaultColorOption, ...colors];

  function renderOptions() {
    return (
      <div
        className="cell-color-picker"
        // biome-ignore lint/a11y/useSemanticElements: Biome wants us to use a select/option here, but the select in the DS is not suitable for our use case as it has no onClick
        role="listbox"
        tabIndex={0}
        aria-label="Change the background color of the cell"
      >
        {combinedColors.map(({ label, value }) => {
          const isVarColour = value.startsWith('var(');
          if (isVarColour) {
            const computedValue = getValueOfCssVariable(value);
            value = computedValue ? computedValue : value;
          }
          return (
            <Dropdown.Item
              onClick={() => {
                onChange({ label, value });
                isTableCellBgActive(editor, value);
              }}
              key={label}
              isChecked={isTableCellBgActive(editor, value)}
              // biome-ignore lint/a11y/useSemanticElements: Biome wants us to use a select/option here, but the select in the DS is not suitable for our use case as it has no onClick
              role="option"
              aria-label={`Select ${label}`}
              aria-selected={isTableCellBgActive(editor, value)}
            >
              <Flex direction="row" alignItems="center" gap="5">
                <Thumbnail
                  variant="content"
                  backgroundColor={value}
                  shape="circle"
                  size="s"
                />
                <Flex direction="column">
                  <span className="colour-name">{label}</span>
                  <span className="subtext">{value}</span>
                </Flex>
              </Flex>
            </Dropdown.Item>
          );
        })}
      </div>
    );
  }

  return <>{renderOptions()}</>;
};
